import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { wrapComponent } from '@components/DynamicSnackBar';
import useDetectSize from '@hooks/useDetectSize';
import { Page } from '@containers/layout';
import { ServiceType, Addon } from '@containers/booking';
import { Box, IconButton } from '@components/common';
import BookingActions, { BookingSelectors } from '@redux/BookingRedux';
import { SubscriptionSelectors } from '@redux/SubscriptionRedux';
import history from '@utils/history';
import ScheduleActions from '@redux/ScheduleRedux';
import ModifyNavbar from './ModifyNavbar';
import ModifyTimeframe from './ModifyTimeframe';
import ModifyConfirm from './ModifyConfirm';
import { BOOKING_STAGE, FREQUENCIES } from '@services/minte';

const MobileFlex = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: ${props => (props.topbarMode ? 'column' : 'row')};
  width: 100%;
  flex: 1;
  justify-content: center;
  align-items: center;

  @media screen and (min-width: 1024px) {
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 48px;
  }

  @media screen and (max-width: 767px) {
    flex-direction: column;
    justify-content: center;
  }
`;

function SubscriptionEdit(props) {
  const {
    activeSubscription,
    initModify,
    bookingStep,
    goToStep,
    resetBooking,
    createSnackbar
  } = props;

  const isMobile = useDetectSize();

  const goToNextStep = () => {
    switch (bookingStep) {
      case BOOKING_STAGE.SERVICE_TYPE:
        goToStep(BOOKING_STAGE.ADDON);
        break;
      case BOOKING_STAGE.ADDON:
        goToStep(BOOKING_STAGE.TIMEFRAME);
        break;
      case BOOKING_STAGE.TIMEFRAME: {
        goToStep(BOOKING_STAGE.CONFIRM);
        break;
      }

      case BOOKING_STAGE.CONFIRM: {
        createSnackbar({
          message: 'Service is successfully updated',
          theme: 'success'
        });
        history.goBack();
        break;
      }
      default:
    }
  };

  const renderContent = () => {
    switch (bookingStep) {
      case BOOKING_STAGE.SERVICE_TYPE:
        return <ServiceType onSubmit={goToNextStep} />;
      case BOOKING_STAGE.TIMEFRAME:
        return <ModifyTimeframe onSubmit={goToNextStep} />;
      case BOOKING_STAGE.ADDON:
        return <Addon onSubmit={goToNextStep} />;
      case BOOKING_STAGE.CONFIRM:
        return <ModifyConfirm onSubmit={goToNextStep} />;
    }
    return null;
  };

  useEffect(() => {
    if (activeSubscription) {
      initModify(activeSubscription);
    }

    return () => {
      resetBooking();
    };
  }, [activeSubscription]);

  const goBack = () => {
    switch (bookingStep) {
      case BOOKING_STAGE.SERVICE_TYPE:
        history.goBack();
        break;
      case BOOKING_STAGE.ADDON:
        goToStep(BOOKING_STAGE.SERVICE_TYPE);
        break;
      case BOOKING_STAGE.CONFIRM:
        goToStep(BOOKING_STAGE.TIMEFRAME);
        break;
      default:
    }
  };

  const renderBackButton = () => {
    return (
      <IconButton
        onClick={goBack}
        name="back"
        width={16}
        height={16}
        title="Back"
      />
    );
  };

  return (
    <Page
      title="Modify"
      renderHeaderLeftButtons={isMobile ? renderBackButton : undefined}
    >
      <MobileFlex topbarMode>
        <ModifyNavbar />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          flex={1}
          width={1}
        >
          {renderContent()}
        </Box>
      </MobileFlex>
    </Page>
  );
}

SubscriptionEdit.propTypes = {
  bookingStep: PropTypes.string,
  resetBooking: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
  createSnackbar: PropTypes.func,
  activeSubscription: PropTypes.object,
  initModify: PropTypes.func
};

const mapStatesToProps = state => ({
  bookingStep: BookingSelectors.selectCurrentStep(state),
  activeSubscription: SubscriptionSelectors.selectActiveSubscription(state)
});

const mapDispatchToProps = dispatch => ({
  resetBooking: () => {
    dispatch(BookingActions.resetBooking());
    dispatch(ScheduleActions.resetRecurringBooking());
  },
  initModify: activeSubscription => {
    dispatch(BookingActions.setBookingFrequency(FREQUENCIES.REGULARLY));
    dispatch(BookingActions.setBookingStep(BOOKING_STAGE.SERVICE_TYPE));
    dispatch(
      ScheduleActions.initRecurringBookingFromSubscription(activeSubscription)
    );
  },
  goToStep: step => dispatch(BookingActions.setBookingStep(step))
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(wrapComponent(SubscriptionEdit));
